import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { AuthShell, ErrorNote } from "./Login";
import { ArrowRight, School, Loader2 } from "lucide-react";

const inputClass = "w-full rounded-2xl border-2 border-[#E5DECF] bg-[#FAF7F2] px-4 py-3.5 text-base font-body text-[#1F2937] outline-none focus:border-[#0D9488]";

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getInviteInfo(token).then((d) => { setInfo(d); if (d.email) setEmail(d.email); }).catch(() => setInfo({ valid: false })).finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { token: jwt, user } = await api.acceptInvite({ token, name: name.trim(), password, email: email.trim() || undefined });
      await setSession(jwt, user);
      navigate("/");
    } catch (err) { setError(formatApiError(err)); } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]"><Loader2 className="animate-spin text-[#0D9488]" /></div>;

  if (!info?.valid) {
    return (
      <AuthShell title="Invite not found" subtitle="This invitation is invalid, already used, or was revoked.">
        <Link to="/login" className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0D9488] px-6 py-4 text-lg font-bold text-white">Go to login</Link>
      </AuthShell>
    );
  }

  const roleWord = info.role === "teacher" ? "teacher" : "school administrator";
  return (
    <AuthShell title={`Join ${info.org_name}`} subtitle={`You've been invited as a ${roleWord}. Create your account to get started.`}>
      <form onSubmit={submit} data-testid="invite-accept-form">
        <ErrorNote message={error} />
        <div className="mb-4 flex items-center gap-2 rounded-2xl border-2 border-[#0D9488] bg-[#0D9488]/5 px-4 py-3 font-bold text-[#0D9488]">
          <School size={18} /> {info.org_name} · {roleWord}
        </div>
        <label className="block text-sm font-bold text-[#1F2937] mb-2">Full name</label>
        <input data-testid="invite-name" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} placeholder="e.g. Ms. Rivera" />
        <label className="block text-sm font-bold text-[#1F2937] mb-2 mt-4">Email</label>
        <input data-testid="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={!!info.email} className={`${inputClass} disabled:opacity-70`} placeholder="you@school.com" />
        <label className="block text-sm font-bold text-[#1F2937] mb-2 mt-4">Password</label>
        <input data-testid="invite-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} placeholder="At least 6 characters" autoComplete="new-password" />
        <button type="submit" disabled={busy} data-testid="invite-submit" className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0D9488] px-6 py-4 text-lg font-bold text-white shadow-md hover:bg-[#0B7F74] disabled:opacity-50">
          {busy ? "Joining..." : "Join & create account"} <ArrowRight size={20} />
        </button>
      </form>
    </AuthShell>
  );
}
